// src/portal-app/operations/TransactionsScreen.js
//
// W3 Task 2 (cramped-screens fix) — Transactions + Voids screen.
//
// Two segmented siblings linked by data lineage:
//   Transactions tab — paginated list with hero, filters, search, void action.
//   Voids tab        — read-only categorised list with polymorphic amounts.
//
// The Voids tab is shown only when hasPermission('portal:voids').
// When the permission is absent, no SegmentedControl renders — just Transactions.
//
// Void eligibility is gated to isCurrentDay:
//   sales_date must equal the current business day's salesDate.
//
// Stale-response guard (seqRef) on both fetches prevents a slow first
// response from overwriting a fast second response after a range flick.
//
// Polymorphic amount extraction for Voids:
//   transaction    → record_data.final_amount || record_data.total_amount
//   expense        → record_data.total_cost
//   salary_advance → record_data.amount
//   salary_payment → record_data.amount
//   staff_allowance→ record_data.total_amount || (record_data.quantity * record_data.unit_price)
//   pending_order  → record_data.total_amount
//   order_tracking → null (no amount)
//   employee_deactivation → null (no amount)
//
// Child tracking grouping:
//   order_tracking voids whose reason contains a pending_order's order_key
//   render nested inside that parent card; standalone ones render at top level.
//
// Presentation rewrite (spec: 2026-08-05-portal-app-redesign-3-cramped-screens,
// Task 2). Seven control rows used to render before a single transaction:
// SegmentedControl, RangeBar, OpSummaryBar, staff FilterRow, payment FilterRow,
// dining FilterRow, SearchInput. Rows 3-7 lived INSIDE SurfaceStates, so a
// filter that produced an empty result hid the very controls needed to clear
// it. Fixed here: hero / control row / active-filter summary now render
// OUTSIDE SurfaceStates, and the three categorical chip rows collapse into
// one FilterButton + FilterSheet. `activeFilterGroup` (dead state from an
// earlier, abandoned attempt at this exact fix) has been removed.
//
// Voids tab is preserved byte-for-byte in behaviour: same permission gate,
// same category FilterRow, same groupVoids hierarchy, same polymorphic
// extractVoidAmount, same read-only cards, same page/page_size pagination.
// Its rows were NOT migrated to CondensedRow — CondensedRow has no slot for
// the parent/child nesting groupVoids relies on, and no dedicated testid for
// the amount node the polymorphic tests assert against; migrating would have
// required rewriting exactly the assertions this task must leave untouched.
//
// Pull-to-refresh (spec §8, all P4 screens) was evaluated and skipped — no
// primitive exists yet anywhere in the kit, and building the gesture +
// animation + a11y contract from scratch here would have doubled the size of
// an already large rewrite. Deferred to Plan 4.
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, {
    useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { transactionService, voidService } from '../../services/api';
import tenantStorage from '../../services/tenantStorage';
import { toLocalISO, rangeForPreset } from '../nav/dateRange';
import { useRangePreset } from '../insights/rangeState';
import { SurfaceStates, fmtMoney, RangeBar } from '../insights/kit';
import {
    SegmentedControl,
    SearchInput,
    FilterRow,
    StatusBadge,
    ConfirmSheet,
} from '../operations/kit';
import {
    CondensedRow, ShareBar, FilterButton, FilterSheet, ensureKitCss,
    IconCash, IconCard,
} from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';

/* ------------------------------------------------------------------ */
/* Screen-local stylesheet                                             */
/* ------------------------------------------------------------------ */
const CSS = `
.txs-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;}
.txs-seg{margin-bottom:14px;}
.txs-list{display:flex;flex-direction:column;gap:10px;}

/* Hero card — the "telling a story" fix: a proportion the eye reads
   instantly instead of two numbers (cash/card) the reader must divide. */
.txs-hero{padding:18px 20px 16px;margin-bottom:12px;}
.txs-hero-label{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:1.2px;color:var(--c-textLight);margin-bottom:7px;}
.txs-hero-value{font-size:38px;font-weight:800;letter-spacing:-1.2px;
  color:var(--c-ink);line-height:1.05;margin-bottom:12px;}
.txs-hero-value:last-child{margin-bottom:0;}

/* Control row — RangeBar (unchanged, shared with Voids) leading,
   FilterButton (transactions only) trailing. */
.txs-control-row{display:flex;align-items:flex-start;gap:8px;margin-bottom:10px;}
.txs-range-wrap{flex:1 1 auto;min-width:0;}
.txs-control-row > .pk-fb{flex:none;margin-block-start:2px;}

/* Search — sits outside SurfaceStates, alongside the other controls */
.txs-search-wrap{margin-bottom:10px;}

/* Active-filter summary line */
.txs-filtersum{display:flex;align-items:center;gap:12px;
  margin:4px 4px 12px;}
.txs-filtersum-text{flex:1 1 auto;min-width:0;font-size:12px;font-weight:600;
  color:var(--c-textLight);overflow:hidden;text-overflow:ellipsis;
  white-space:nowrap;}
.txs-filtersum-clear{flex:none;min-height:44px;padding-inline:6px;border:none;
  background:transparent;font-family:inherit;font-size:12px;font-weight:700;
  color:var(--c-green);cursor:pointer;}

/* Count line */
.txs-count-line{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:1.2px;color:var(--c-textLight);margin:2px 4px 10px;}

/* Transaction row wrap — CondensedRow plus the void action beneath it */
.txs-tx-wrap{display:flex;flex-direction:column;gap:6px;}
.txs-void-btn{align-self:flex-start;min-height:44px;padding:0 14px;
  margin-inline-start:44px;border:1.5px solid var(--c-red);
  border-radius:12px;background:color-mix(in srgb,var(--c-red) 8%,transparent);
  font-family:inherit;font-size:12px;font-weight:700;color:var(--c-red);
  cursor:pointer;white-space:nowrap;
  transition:background .15s ease,transform .12s ease;}
.txs-void-btn:active{transform:scale(.97);}
.txs-void-error{font-size:12px;font-weight:600;color:var(--c-red);
  margin-top:4px;padding:0 4px;}

/* Load more */
.txs-load-more{display:flex;justify-content:center;padding:16px 0;}
.txs-load-more-btn{min-height:44px;padding:0 24px;border:1.5px solid
  color-mix(in srgb,var(--c-ink) 14%,transparent);border-radius:16px;
  background:color-mix(in srgb,var(--c-surface) 40%,transparent);
  font-family:inherit;font-size:14px;font-weight:600;color:var(--c-ink);
  cursor:pointer;transition:background .15s ease,transform .12s ease;}
.txs-load-more-btn:active{transform:scale(.97);}
.txs-load-more-btn:disabled{opacity:.5;cursor:not-allowed;}

/* Void card (Voids tab — unchanged, not migrated to CondensedRow) */
.vds-card{padding:14px 16px;margin-bottom:2px;}
.vds-card-row{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;}
.vds-card-body{flex:1;min-width:0;}
.vds-meta{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-bottom:6px;}
.vds-auth{font-size:12px;font-weight:600;color:var(--c-textLight);}
.vds-time{font-size:12px;font-weight:500;color:var(--c-mutedLight);}
.vds-reason{font-size:13px;font-weight:500;color:var(--c-textLight);
  margin-top:4px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.vds-amount{font-size:16px;font-weight:800;color:var(--c-ink);flex:none;}
.vds-children{margin-top:10px;padding-inline-start:12px;
  border-inline-start:2px solid color-mix(in srgb,var(--c-ink) 10%,transparent);
  display:flex;flex-direction:column;gap:8px;}
.vds-child-card{padding:10px 14px;}
.vds-child-row{display:flex;align-items:center;justify-content:space-between;gap:8px;}
.vds-child-reason{font-size:12px;font-weight:500;color:var(--c-textLight);}
.vds-child-auth{font-size:11px;font-weight:600;color:var(--c-mutedLight);}

/* Date/time formatting helper */
.txs-del-at{font-size:11px;font-weight:500;color:var(--c-mutedLight);}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'txs-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Polymorphic amount extraction                                        */
/* ------------------------------------------------------------------ */

/** @returns {number|null} the dollar amount to display, or null if N/A */
export function extractVoidAmount(voidType, recordData) {
    const d = recordData || {};
    switch (voidType) {
        case 'transaction':
            return d.final_amount != null ? Number(d.final_amount) : (d.total_amount != null ? Number(d.total_amount) : null);
        case 'expense':
            return d.total_cost != null ? Number(d.total_cost) : null;
        case 'salary_advance':
        case 'salary_payment':
            return d.amount != null ? Number(d.amount) : null;
        case 'staff_allowance':
            if (d.total_amount != null) return Number(d.total_amount);
            if (d.quantity != null && d.unit_price != null) return Number(d.quantity) * Number(d.unit_price);
            return null;
        case 'pending_order':
            return d.total_amount != null ? Number(d.total_amount) : null;
        case 'order_tracking':
        case 'employee_deactivation':
        default:
            return null;
    }
}

/* ------------------------------------------------------------------ */
/* Void type category filter groups                                     */
/* ------------------------------------------------------------------ */
const VOID_CATEGORY_GROUPS = {
    financial: 'transaction,expense,salary_advance,salary_payment',
    operational: 'pending_order,order_tracking',
    staff: 'employee_deactivation,staff_allowance',
};

/* ------------------------------------------------------------------ */
/* Payment method tone map                                             */
/* ------------------------------------------------------------------ */
function paymentTone(method) {
    switch ((method || '').toLowerCase()) {
        case 'card': return 'blue';
        case 'cash': return 'green';
        default: return 'muted';
    }
}

/* ------------------------------------------------------------------ */
/* Payment method icon — CondensedRow's 32px icon slot was rendering   */
/* blank (no icon prop passed) and eating that space for nothing.     */
/* Cash gets its own glyph; everything else (card, split payment, …)  */
/* uses the card glyph as the closest general-purpose "payment" mark. */
/* ------------------------------------------------------------------ */
function paymentIcon(method) {
    return (method || '').toLowerCase() === 'cash' ? <IconCash /> : <IconCard />;
}

/* ------------------------------------------------------------------ */
/* Void type tone map                                                   */
/* ------------------------------------------------------------------ */
function voidTone(voidType) {
    switch (voidType) {
        case 'transaction': return 'red';
        case 'expense': return 'amber';
        case 'salary_advance':
        case 'salary_payment': return 'amber';
        case 'pending_order': return 'red';
        case 'order_tracking': return 'muted';
        case 'employee_deactivation': return 'muted';
        case 'staff_allowance': return 'amber';
        default: return 'muted';
    }
}

/* ------------------------------------------------------------------ */
/* Format a deleted_at ISO string to readable local date/time          */
/* ------------------------------------------------------------------ */
function fmtDeletedAt(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        // Local date/time — never toISOString
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch (e) {
        return iso;
    }
}

/* ------------------------------------------------------------------ */
/* Child tracking grouping                                             */
/* Matches order_tracking voids to their parent pending_order by       */
/* checking if the child reason contains the parent's order_key.       */
/* ------------------------------------------------------------------ */
function groupVoids(items) {
    // Collect all pending_order parents (by order_key in record_data)
    const parents = items.filter((v) => v.void_type === 'pending_order');
    const parentKeys = parents.map((p) => {
        const key = (p.record_data || {}).order_key;
        return { void: p, key };
    }).filter((x) => x.key);

    const claimed = new Set(); // void_ids claimed as children

    // For each pending_order, find matching order_tracking children
    const parentWithChildren = parents.map((parent) => {
        const key = (parent.record_data || {}).order_key;
        let children = [];
        if (key) {
            children = items.filter(
                (v) =>
                    v.void_type === 'order_tracking' &&
                    v.reason &&
                    v.reason.includes(key),
            );
            children.forEach((c) => claimed.add(c.void_id));
        }
        return { parent, children };
    });

    // Standalone items: not a pending_order and not claimed as a child
    const standalones = items.filter(
        (v) =>
            v.void_type !== 'pending_order' &&
            !claimed.has(v.void_id),
    );

    return { parentWithChildren, standalones };
}

/* ------------------------------------------------------------------ */
/* Transaction Row — CondensedRow plus the void action beneath it.     */
/* Every field the old TransactionCard showed is still here: time,     */
/* staff (the row's name), payment method (the row's icon + pill),     */
/* amount (the row's value), products summary + dining option (the     */
/* row's secondary text), and the void button where eligible. Dining   */
/* option moved from the name to the secondary line — see below.       */
/* ------------------------------------------------------------------ */
function TransactionRow({ tx, currency, isCurrentDay, salesDate, onVoidPress, t }) {
    // Void-eligible: business day is open AND transaction is from today
    const canVoid = isCurrentDay && tx.sales_date === salesDate;
    const time = tx.time ? tx.time.slice(0, 5) : '—';
    // Name is time · staff ONLY — dining_option used to be folded in here too,
    // but at ~30 characters of budget on a 393px row (before the icon slot
    // below was even filled in) "12:30 · Christopher Lee · Dine In" already
    // overflowed and silently dropped the dining option with no way to see
    // it (no title, nowrap+ellipsis). Moved to the secondary line instead,
    // alongside products_summary, where CondensedRow now also sets a title
    // attribute so anything still truncated stays reachable on hover/long-press.
    const name = `${time} · ${tx.staff_name}`;
    const secondary = [tx.products_summary, tx.dining_option].filter(Boolean).join(' · ') || undefined;

    return (
        <div className="txs-tx-wrap" data-testid={`tx-card-${tx.transaction_id}`}>
            <CondensedRow
                icon={paymentIcon(tx.payment_method)}
                tone={paymentTone(tx.payment_method)}
                name={name}
                value={fmtMoney(tx.final_amount, currency)}
                secondary={secondary}
                pill={{ label: tx.payment_method, tone: paymentTone(tx.payment_method) }}
                testId={tx.transaction_id}
            />
            {canVoid && (
                <button
                    type="button"
                    className="txs-void-btn"
                    data-testid={`void-btn-${tx.transaction_id}`}
                    onClick={() => onVoidPress(tx)}
                >
                    {t('void_transaction', {}, 'Void')}
                </button>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Void Card — unchanged, not migrated to CondensedRow. CondensedRow   */
/* has no slot for groupVoids' parent/child nesting or for the         */
/* dedicated void-amount testid the polymorphic tests assert against;  */
/* migrating would have required rewriting exactly the tests this task */
/* must leave untouched. See file header.                              */
/* ------------------------------------------------------------------ */
function VoidCard({ vd, currency, t, children }) {
    const amount = extractVoidAmount(vd.void_type, vd.record_data);

    return (
        <GlassCard className="vds-card" data-testid={`void-card-${vd.void_id}`}>
            <div className="vds-card-row">
                <div className="vds-card-body">
                    <div className="vds-meta">
                        <StatusBadge
                            status={vd.void_type}
                            label={vd.void_type.replace(/_/g, ' ')}
                            tone={voidTone(vd.void_type)}
                        />
                        {vd.authorized_by && (
                            <span className="vds-auth">
                                {t('authorized_by', {}, 'By')}: {vd.authorized_by}
                            </span>
                        )}
                        <span className="txs-del-at">{fmtDeletedAt(vd.deleted_at)}</span>
                    </div>
                    {vd.reason && (
                        <div className="vds-reason" title={vd.reason}>{vd.reason}</div>
                    )}
                </div>
                {amount != null && (
                    <span
                        className="vds-amount opk-num"
                        data-testid="void-amount"
                    >
                        {fmtMoney(amount, currency)}
                    </span>
                )}
            </div>
            {children && (
                <div className="vds-children">{children}</div>
            )}
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* VoidChildCard — compact nested card for order_tracking children     */
/* ------------------------------------------------------------------ */
function VoidChildCard({ vd, t }) {
    return (
        <GlassCard className="vds-child-card" data-testid={`void-card-${vd.void_id}`}>
            <div className="vds-child-row">
                <div>
                    <StatusBadge
                        status={vd.void_type}
                        label={vd.void_type.replace(/_/g, ' ')}
                        tone={voidTone(vd.void_type)}
                    />
                    {vd.reason && (
                        <div className="vds-child-reason">{vd.reason}</div>
                    )}
                </div>
                {vd.authorized_by && (
                    <span className="vds-child-auth">{vd.authorized_by}</span>
                )}
            </div>
        </GlassCard>
    );
}

/* ================================================================== */
/* Main screen component                                                */
/* ================================================================== */
export default function TransactionsScreen() {
    ensureCss();
    ensureKitCss();
    const auth = useAuth() || {};
    const currency = auth.getCurrency ? auth.getCurrency() : '£';
    const hasVoids = auth.hasPermission ? auth.hasPermission('portal:voids') : false;
    const username = auth.username || '';
    const { t, isRTL } = useLanguage();

    // Shared range preset (survives pops / tab switches)
    const [preset, setPreset] = useRangePreset();
    const range = useMemo(() => rangeForPreset(preset), [preset]);
    const startIso = toLocalISO(range.start);
    const endIso = toLocalISO(range.end);

    // Active segment
    const [activeTab, setActiveTab] = useState('transactions');

    // ── Transactions state ─────────────────────────────────────────────
    const [txStatus, setTxStatus] = useState('loading');
    const [txItems, setTxItems] = useState([]);
    const [txTotal, setTxTotal] = useState(0);
    const [txSummary, setTxSummary] = useState(null);
    const [txFilters, setTxFilters] = useState(null); // loaded from first response
    const [txOffset, setTxOffset] = useState(0);
    const TX_LIMIT = 25;

    // Filter state — staff, payment method, dining option
    const [staffFilter, setStaffFilter] = useState(null);
    const [paymentFilter, setPaymentFilter] = useState(null);
    const [diningFilter, setDiningFilter] = useState(null);
    const [search, setSearch] = useState('');
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);

    // Business day state for void eligibility
    const [businessDayState, setBusinessDayState] = useState({ isBusinessDay: false, salesDate: null });

    // Void sheet state
    const [voidTarget, setVoidTarget] = useState(null); // the tx being voided
    const [voidReason, setVoidReason] = useState('');
    const [voidLoading, setVoidLoading] = useState(false);
    const [voidError, setVoidError] = useState('');

    // ── Voids state ────────────────────────────────────────────────────
    const [voidsStatus, setVoidsStatus] = useState('loading');
    const [voidsItems, setVoidsItems] = useState([]);
    const [voidsTotal, setVoidsTotal] = useState(0);
    const [voidsPage, setVoidsPage] = useState(1);
    const VOIDS_PAGE_SIZE = 50;
    const [voidTypeFilter, setVoidTypeFilter] = useState(null); // null = All

    // ── Sequence refs (stale response guard) ──────────────────────────
    const txSeqRef = useRef(0);
    const voidsSeqRef = useRef(0);

    // ── Load business day state once ──────────────────────────────────
    useEffect(() => {
        tenantStorage.getBusinessDayState().then((state) => {
            setBusinessDayState(state || { isBusinessDay: false, salesDate: null });
        }).catch(() => {
            setBusinessDayState({ isBusinessDay: false, salesDate: null });
        });
    }, []);

    // ── Transactions fetch ────────────────────────────────────────────
    const loadTransactions = useCallback((offset = 0, append = false) => {
        txSeqRef.current += 1;
        const seq = txSeqRef.current;
        if (!append) setTxStatus('loading');

        const params = {
            startDate: startIso,
            endDate: endIso,
            limit: TX_LIMIT,
            offset,
            sortField: 'time',
            sortDir: 'desc',
        };
        if (staffFilter) params.staff = staffFilter;
        if (paymentFilter) params.paymentMethod = paymentFilter;
        if (diningFilter) params.diningOption = diningFilter;
        if (search) params.search = search;

        transactionService.getTransactionsRange(params)
            .then((data) => {
                if (seq !== txSeqRef.current) return; // stale
                const items = data.items || [];
                setTxItems((prev) => append ? [...prev, ...items] : items);
                setTxTotal(data.total || 0);
                setTxSummary(data.summary || null);
                if (!append && data.filters) setTxFilters(data.filters);
                setTxStatus(items.length === 0 && !append ? 'empty' : 'ready');
            })
            .catch(() => {
                if (seq === txSeqRef.current) setTxStatus('error');
            });
    }, [startIso, endIso, staffFilter, paymentFilter, diningFilter, search]);

    // Re-fetch when range, filters, or search change (reset offset)
    useEffect(() => {
        setTxOffset(0);
        loadTransactions(0, false);
    }, [loadTransactions]);

    const handleLoadMoreTx = () => {
        const newOffset = txOffset + TX_LIMIT;
        setTxOffset(newOffset);
        loadTransactions(newOffset, true);
    };

    // ── Voids fetch ───────────────────────────────────────────────────
    const loadVoids = useCallback((page = 1, append = false) => {
        voidsSeqRef.current += 1;
        const seq = voidsSeqRef.current;
        if (!append) setVoidsStatus('loading');

        const params = {
            start_date: `${startIso}T00:00:00`,
            end_date: `${endIso}T23:59:59`,
            page,
            page_size: VOIDS_PAGE_SIZE,
        };
        if (voidTypeFilter) {
            // voidTypeFilter is the category group key; map to comma-joined void_types
            params.void_type = VOID_CATEGORY_GROUPS[voidTypeFilter] || voidTypeFilter;
        }

        voidService.getVoids(params)
            .then((data) => {
                if (seq !== voidsSeqRef.current) return; // stale
                const items = data.items || [];
                setVoidsItems((prev) => append ? [...prev, ...items] : items);
                setVoidsTotal(data.total || 0);
                setVoidsStatus(items.length === 0 && !append ? 'empty' : 'ready');
            })
            .catch(() => {
                if (seq === voidsSeqRef.current) setVoidsStatus('error');
            });
    }, [startIso, endIso, voidTypeFilter]);

    // Load voids when tab is active or filter/range changes
    useEffect(() => {
        if (!hasVoids || activeTab !== 'voids') return;
        setVoidsPage(1);
        loadVoids(1, false);
    }, [activeTab, hasVoids, loadVoids]);

    const handleLoadMoreVoids = () => {
        const newPage = voidsPage + 1;
        setVoidsPage(newPage);
        loadVoids(newPage, true);
    };

    // ── Void action ───────────────────────────────────────────────────
    const handleVoidPress = (tx) => {
        setVoidTarget(tx);
        setVoidReason('');
        setVoidError('');
    };

    const handleVoidConfirm = async () => {
        if (!voidTarget) return;
        setVoidLoading(true);
        setVoidError('');
        try {
            await transactionService.deleteTransaction(voidTarget.transaction_id, voidReason, username);
            setVoidTarget(null);
            setVoidReason('');
            // Reload from start
            setTxOffset(0);
            loadTransactions(0, false);
        } catch (err) {
            setVoidError(t('void_error', {}, 'Failed to void transaction. Please try again.'));
        } finally {
            setVoidLoading(false);
        }
    };

    const handleVoidClose = () => {
        if (voidLoading) return;
        setVoidTarget(null);
        setVoidReason('');
        setVoidError('');
    };

    // ── Derived: hero revenue + cash/card split ────────────────────────
    const heroRevenue = fmtMoney(txSummary ? txSummary.revenue : 0, currency);
    const cashValue = Number((txSummary && txSummary.cash) || 0);
    const cardValue = Number((txSummary && txSummary.card) || 0);
    const showShareBar = (cashValue + cardValue) > 0;
    const shareSegments = [
        { id: 'cash', label: t('cash', {}, 'Cash'), value: cashValue, display: fmtMoney(cashValue, currency), tone: 'green' },
        { id: 'card', label: t('card', {}, 'Card'), value: cardValue, display: fmtMoney(cardValue, currency), tone: 'blue' },
    ];
    const shareAriaLabel = t(
        'cash_card_split_aria',
        { cash: fmtMoney(cashValue, currency), card: fmtMoney(cardValue, currency) },
        'Cash {cash}, Card {card}',
    );

    // ── Derived: filter chips from first response ─────────────────────
    const staffChips = useMemo(
        () => (txFilters && txFilters.staff ? txFilters.staff.map((s) => ({ id: s, label: s })) : []),
        [txFilters],
    );
    const paymentChips = useMemo(
        () => (txFilters && txFilters.payment_methods
            ? txFilters.payment_methods.map((p) => ({ id: p, label: p }))
            : []),
        [txFilters],
    );
    const diningChips = useMemo(
        () => (txFilters && txFilters.dining_options
            ? txFilters.dining_options.map((d) => ({ id: d, label: d }))
            : []),
        [txFilters],
    );

    // ── Derived: FilterSheet groups + active-filter summary ───────────
    const filterGroups = useMemo(() => {
        const groups = [];
        if (staffChips.length > 0) groups.push({ id: 'staff', label: t('staff', {}, 'Staff'), options: staffChips });
        if (paymentChips.length > 0) groups.push({ id: 'payment', label: t('payment_method', {}, 'Payment'), options: paymentChips });
        if (diningChips.length > 0) groups.push({ id: 'dining', label: t('dining_option', {}, 'Dining'), options: diningChips });
        return groups;
    }, [staffChips, paymentChips, diningChips, t]);

    const filterValue = { staff: staffFilter, payment: paymentFilter, dining: diningFilter };
    const activeFilterCount = [staffFilter, paymentFilter, diningFilter].filter(Boolean).length;
    const activeFilterText = [staffFilter, paymentFilter, diningFilter].filter(Boolean).join(' · ');

    const handleFilterChange = (groupId, optionId) => {
        if (groupId === 'staff') setStaffFilter(optionId);
        else if (groupId === 'payment') setPaymentFilter(optionId);
        else if (groupId === 'dining') setDiningFilter(optionId);
    };

    const clearAllFilters = () => {
        setStaffFilter(null);
        setPaymentFilter(null);
        setDiningFilter(null);
        setSearch('');
    };

    // ── Derived: voids grouped ────────────────────────────────────────
    const voidsGrouped = useMemo(() => groupVoids(voidsItems), [voidsItems]);

    // ── Segment options ───────────────────────────────────────────────
    const segOptions = [
        { id: 'transactions', label: t('hub_transactions', {}, 'Transactions') },
        { id: 'voids', label: t('voids_tab', {}, 'Voids') },
    ];

    // ── Void type filter chips for Voids tab ──────────────────────────
    const voidCategoryChips = [
        { id: 'financial', label: t('financial_voids', {}, 'Financial') },
        { id: 'operational', label: t('operational_voids', {}, 'Operational') },
        { id: 'staff', label: t('staff_voids', {}, 'Staff') },
    ];

    // ── Business day info ─────────────────────────────────────────────
    const isCurrentDay = !!(businessDayState.isBusinessDay && businessDayState.salesDate);
    const salesDate = businessDayState.salesDate || '';

    return (
        <div className="txs-wrap" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* SegmentedControl only visible when portal:voids permitted */}
            {hasVoids && (
                <div className="txs-seg">
                    <SegmentedControl
                        options={segOptions}
                        value={activeTab}
                        onChange={setActiveTab}
                    />
                </div>
            )}

            {/* Hero card — revenue as a large figure, cash/card split as a
                ShareBar. Transactions-only; OUTSIDE SurfaceStates so it never
                vanishes. */}
            {activeTab === 'transactions' && (
                <GlassCard veil className="txs-hero" data-testid="tx-hero">
                    <div className="txs-hero-label">{t('taken_this_period', {}, 'Taken This Period')}</div>
                    <div className="txs-hero-value opk-num" data-testid="hero-revenue">{heroRevenue}</div>
                    {showShareBar && (
                        <ShareBar segments={shareSegments} ariaLabel={shareAriaLabel} />
                    )}
                </GlassCard>
            )}

            {/* Control row — RangeBar sits OUTSIDE SurfaceStates for both
                tabs (usable from error/empty); FilterButton is transactions
                only. */}
            <div className="txs-control-row">
                <div className="txs-range-wrap">
                    <RangeBar value={preset} onChange={(id) => { setPreset(id); }} />
                </div>
                {activeTab === 'transactions' && (
                    <FilterButton
                        activeCount={activeFilterCount}
                        onPress={() => setFilterSheetOpen(true)}
                    />
                )}
            </div>

            {/* Active-filter summary — OUTSIDE SurfaceStates so a filtered-
                to-empty result still shows the means to clear the filter. */}
            {activeTab === 'transactions' && activeFilterCount > 0 && (
                <div className="txs-filtersum">
                    <span className="txs-filtersum-text">{activeFilterText}</span>
                    <button
                        type="button"
                        className="txs-filtersum-clear"
                        data-testid="clear-filters"
                        onClick={clearAllFilters}
                    >
                        {t('clear', {}, 'Clear')}
                    </button>
                </div>
            )}

            {/* Search — OUTSIDE SurfaceStates, alongside the other controls.
                Search is a filter: a term that matches nothing flips status to
                'empty', and SurfaceStates returns early for that status without
                rendering children. If the field lived inside, typing a term
                with no matches would unmount it mid-keystroke — the user could
                neither finish typing nor clear it, and clearAllFilters doesn't
                touch search either. Keeping it here (like RangeBar/FilterButton
                above) means it always survives its own result. */}
            {activeTab === 'transactions' && (
                <div className="txs-search-wrap">
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        placeholder={t('search_transactions', {}, 'Search transactions…')}
                        debounce={300}
                    />
                </div>
            )}

            {/* ── Transactions Tab ── */}
            {activeTab === 'transactions' && (
                <SurfaceStates
                    status={txStatus}
                    onRetry={() => loadTransactions(0, false)}
                    emptyTitle={t('no_transactions', {}, 'No transactions found')}
                    emptyMsg={t('no_transactions_msg', {}, 'Try a different date range or filter.')}
                >
                    {/* Count line */}
                    <div className="txs-count-line" data-testid="tx-count-line">
                        {t('n_transactions', { count: txTotal }, '{count} Transactions')}
                    </div>

                    {/* Transaction list */}
                    <div className="txs-list">
                        {txItems.map((tx) => (
                            <TransactionRow
                                key={tx.transaction_id}
                                tx={tx}
                                currency={currency}
                                isCurrentDay={isCurrentDay}
                                salesDate={salesDate}
                                onVoidPress={handleVoidPress}
                                t={t}
                            />
                        ))}
                    </div>

                    {/* Load more */}
                    {txItems.length < txTotal && (
                        <div className="txs-load-more">
                            <button
                                type="button"
                                className="txs-load-more-btn"
                                data-testid="load-more-btn"
                                onClick={handleLoadMoreTx}
                            >
                                {t('load_more', {}, 'Load more')}
                            </button>
                        </div>
                    )}
                </SurfaceStates>
            )}

            {/* ── Voids Tab ── */}
            {activeTab === 'voids' && hasVoids && (
                <SurfaceStates
                    status={voidsStatus}
                    onRetry={() => loadVoids(1, false)}
                    emptyTitle={t('no_voids', {}, 'No voids found')}
                    emptyMsg={t('no_voids_msg', {}, 'No records have been voided in this period.')}
                >
                    {/* Void type category filters */}
                    <FilterRow
                        filters={voidCategoryChips}
                        value={voidTypeFilter}
                        onChange={setVoidTypeFilter}
                        allLabel={t('all', {}, 'All')}
                    />

                    {/* Void list */}
                    <div className="txs-list" style={{ marginTop: 12 }}>
                        {/* Parent pending_order voids with their children */}
                        {voidsGrouped.parentWithChildren.map(({ parent, children }) => (
                            <VoidCard
                                key={parent.void_id}
                                vd={parent}
                                currency={currency}
                                t={t}
                            >
                                {children.length > 0 && children.map((child) => (
                                    <VoidChildCard key={child.void_id} vd={child} t={t} />
                                ))}
                            </VoidCard>
                        ))}
                        {/* Standalone voids (non-pending_order + unclaimed order_tracking) */}
                        {voidsGrouped.standalones.map((vd) => (
                            <VoidCard key={vd.void_id} vd={vd} currency={currency} t={t} />
                        ))}
                    </div>

                    {/* Load more */}
                    {voidsItems.length < voidsTotal && (
                        <div className="txs-load-more">
                            <button
                                type="button"
                                className="txs-load-more-btn"
                                data-testid="load-more-voids-btn"
                                onClick={handleLoadMoreVoids}
                            >
                                {t('load_more', {}, 'Load more')}
                            </button>
                        </div>
                    )}
                </SurfaceStates>
            )}

            {/* ── Void ConfirmSheet ── */}
            <ConfirmSheet
                open={!!voidTarget}
                onClose={handleVoidClose}
                onConfirm={handleVoidConfirm}
                title={t('void_transaction', {}, 'Void Transaction')}
                message={t('void_confirm_msg', {}, 'This action cannot be undone. The transaction will be voided and stock will be restored.')}
                confirmLabel={t('void_transaction', {}, 'Void')}
                destructive
                loading={voidLoading}
                reasonLabel={t('void_reason_label', {}, 'Reason for void')}
                reasonValue={voidReason}
                onReasonChange={setVoidReason}
            >
                {/* Void error — rendered INSIDE the sheet. Previously this sat
                    as a sibling after ConfirmSheet, behind its fixed,
                    z-indexed backdrop, and was invisible in the live app. */}
                {voidError && (
                    <div className="txs-void-error" data-testid="void-error">
                        {voidError}
                    </div>
                )}
            </ConfirmSheet>

            {/* ── Filter sheet — staff / payment / dining ── */}
            <FilterSheet
                open={filterSheetOpen}
                onClose={() => setFilterSheetOpen(false)}
                groups={filterGroups}
                value={filterValue}
                onChange={handleFilterChange}
                onClear={clearAllFilters}
            />
        </div>
    );
}
